import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { todayString } from '../../utils/appDateTime';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { INDIAN_STATES, INQUIRY_STATUSES, BOARD_OPTIONS } from '../../data/indianStates';
import { canAssignCrmInquiries, canDeleteCrmInquiries } from '../../utils/crmPermissions';
import { validateMobile } from '../../utils/phoneValidation';
import MobileInput from '../ui/mobile-input';
import { Plus, RefreshCw } from 'lucide-react';
import AsyncSubmitButton, { ASYNC_SUBMIT_STATUS } from '../ui/AsyncSubmitButton';
import CrmConvertJoinDialog from './CrmConvertJoinDialog';

const REQUIRED_INQUIRY_FIELDS = [
  { key: 'school_name', label: 'School name' },
  { key: 'principal_name', label: 'Principal' },
  { key: 'email', label: 'Email' },
  { key: 'state', label: 'State' },
  { key: 'district', label: 'District' },
  { key: 'city', label: 'City' },
];

function validateInquiryForm(form) {
  for (const { key, label } of REQUIRED_INQUIRY_FIELDS) {
    if (!String(form[key] ?? '').trim()) {
      return `${label} is required`;
    }
  }
  const contactErr = validateMobile(form.contact_number, { required: true });
  if (contactErr) return `Contact: ${contactErr}`;
  const whatsappErr = validateMobile(form.whatsapp_number, { required: true });
  if (whatsappErr) return `WhatsApp: ${whatsappErr}`;
  const email = String(form.email).trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Enter a valid email address';
  }
  return null;
}

const emptyInquiry = () => ({
  school_name: '', principal_name: '', contact_number: '', whatsapp_number: '',
  email: '', state: '', district: '', city: '', full_address: '',
  student_strength: '', board: '', existing_software: '', interested_product: '',
  inquiry_date: todayString(), next_followup_date: '',
  remarks: '', status: 'new', employee_id: '',
});

function employeeLabel(emp) {
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  return name ? `${name} (${emp.employee_code})` : emp.employee_code;
}

export default function CrmInquiries() {
  const { toast } = useToast();
  const { user } = useAuth();
  const location = useLocation();
  const canAssign = canAssignCrmInquiries(user);
  const canDelete = canDeleteCrmInquiries(user);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyInquiry());
  const [editing, setEditing] = useState(null);
  const [convertId, setConvertId] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(ASYNC_SUBMIT_STATUS.idle);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/crm/inquiries', { params });
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    if (!canAssign) return;
    try {
      const res = await api.get('/crm/employees');
      const list = (res.data?.data?.items || []).filter((e) => e.status === 'active');
      setEmployees(list);
    } catch {
      setEmployees([]);
    }
  };

  useEffect(() => { fetchItems(); }, [statusFilter]);
  useEffect(() => { fetchEmployees(); }, [canAssign]);

  const openEdit = (row) => {
    setEditing(row);
    setSubmitStatus(ASYNC_SUBMIT_STATUS.idle);
    setForm({
      ...emptyInquiry(),
      ...row,
      employee_id: row.employee_id ? String(row.employee_id) : '',
    });
    setOpen(true);
  };

  useEffect(() => {
    const editId = location.state?.editId;
    if (!editId || items.length === 0) return;
    const row = items.find((i) => String(i.id) === String(editId));
    if (row) openEdit(row);
  }, [items, location.state?.editId]);

  const buildPayload = () => {
    const payload = { ...form };
    if (editing) {
      delete payload.employee_id;
    } else if (canAssign) {
      payload.employee_id = form.employee_id ? Number(form.employee_id) : null;
    } else {
      delete payload.employee_id;
    }
    return payload;
  };

  const handleAssign = async (inquiryId, value) => {
    const employeeId = value === 'unassigned' ? null : Number(value);
    setAssigningId(inquiryId);
    try {
      const res = await api.put(`/crm/inquiries/${inquiryId}`, { employee_id: employeeId });
      const updated = res.data?.data;
      if (updated) {
        setItems((prev) => prev.map((item) => (item.id === inquiryId ? updated : item)));
      } else {
        fetchItems();
      }
      toast({ title: 'Assignment updated' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setAssigningId(null);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/crm/inquiries/${id}`);
      toast({ title: 'Moved to Bin', description: 'Inquiry can be restored from Bin.' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const save = async ({ beginLoading }) => {
    const validationError = validateInquiryForm(form);
    if (validationError) {
      toast({ title: 'Validation Error', description: validationError, variant: 'destructive' });
      return;
    }
    beginLoading();
    try {
      const payload = buildPayload();
      if (editing) {
        await api.put(`/crm/inquiries/${editing.id}`, payload);
        toast({ title: 'Updated' });
      } else {
        await api.post('/crm/inquiries', payload);
        toast({ title: 'Inquiry created' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Save failed', variant: 'destructive' });
      throw e;
    }
  };

  return (
    <div className="space-y-6" data-testid="crm-inquiries">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">School Inquiries</h1>
          <p className="text-muted-foreground">Active sales pipeline — converted schools are in Schools</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {INQUIRY_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchItems} className="shrink-0"><RefreshCw className="w-4 h-4" /></Button>
          <Button className="bg-blue-600 w-full sm:w-auto" onClick={() => { setEditing(null); setSubmitStatus(ASYNC_SUBMIT_STATUS.idle); setForm(emptyInquiry()); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Add Inquiry
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Inquiries ({items.length})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {loading ? <p>Loading…</p> : items.length === 0 ? <p className="text-muted-foreground">No inquiries.</p> : items.map((row) => (
            <div key={row.id} className="border border-border rounded-lg p-4 flex flex-wrap justify-between gap-2">
              <div>
                <p className="font-semibold">{row.school_name}</p>
                <p className="text-sm text-muted-foreground">{row.principal_name} · {row.city}, {row.state}</p>
                <p className="text-sm">{row.contact_number} · {row.email}</p>
                <div className="text-sm text-muted-foreground mt-1">
                  <Label className="text-muted-foreground font-normal">Assigned To</Label>
                  {canAssign ? (
                    <Select
                      value={row.employee_id ? String(row.employee_id) : 'unassigned'}
                      onValueChange={(v) => handleAssign(row.id, v)}
                      disabled={assigningId === row.id}
                    >
                      <SelectTrigger className="mt-1 h-8 max-w-xs"><SelectValue placeholder="Select employee" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={String(emp.id)}>{employeeLabel(emp)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-foreground">{row.assigned_to_name || 'Unassigned'}</p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Inquiry Added By: <span className="text-foreground">{row.created_by_label || 'Unknown'}</span>
                </p>
                <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">{row.status}</span>
                {row.next_followup_date && <span className="ml-2 text-xs text-amber-600">Follow-up: {row.next_followup_date}</span>}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start w-full sm:w-auto">
                <Button size="sm" variant="outline" className="w-full sm:w-auto" asChild>
                  <Link to={`/crm/inquiries/${row.id}`}>View</Link>
                </Button>
                <Button size="sm" variant="outline" className="w-full sm:w-auto" onClick={() => openEdit(row)}>Edit</Button>
                {!row.tenant_school_id && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 w-full sm:w-auto" onClick={() => setConvertId(row.id)}>
                    Convert to Join
                  </Button>
                )}
                {canDelete && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="destructive" className="w-full sm:w-auto">Delete</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete inquiry?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => remove(row.id)}
                          className="bg-destructive text-destructive-foreground"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
          <DialogHeader><DialogTitle>{editing ? 'Edit Inquiry' : 'New Inquiry'}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {canAssign && !editing && (
              <div>
                <Label>Assign To</Label>
                <Select
                  value={form.employee_id || 'unassigned'}
                  onValueChange={(v) => setForm({ ...form, employee_id: v === 'unassigned' ? '' : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={String(emp.id)}>{employeeLabel(emp)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editing && (
              <div>
                <Label>Inquiry Added By</Label>
                <Input value={editing.created_by_label || 'Unknown'} readOnly disabled className="bg-muted" />
              </div>
            )}
            <div><Label>School name *</Label><Input value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Principal *</Label><Input value={form.principal_name} onChange={(e) => setForm({ ...form, principal_name: e.target.value })} /></div>
              <div><Label>Contact *</Label><MobileInput value={form.contact_number} onChange={(v) => setForm({ ...form, contact_number: v })} required /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>WhatsApp *</Label><MobileInput value={form.whatsapp_number} onChange={(v) => setForm({ ...form, whatsapp_number: v })} required /></div>
            </div>
            <div><Label>State *</Label>
              <Select value={form.state || ''} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent>{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>District *</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></div>
              <div><Label>City *</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            </div>
            <div><Label>Board</Label>
              <Select value={form.board || ''} onValueChange={(v) => setForm({ ...form, board: v })}>
                <SelectTrigger><SelectValue placeholder="Board" /></SelectTrigger>
                <SelectContent>{BOARD_OPTIONS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Inquiry date</Label><DateInput value={form.inquiry_date} onChange={(v) => setForm({ ...form, inquiry_date: v })} /></div>
              <div><Label>Next follow-up</Label><DateInput value={form.next_followup_date || ''} onChange={(v) => setForm({ ...form, next_followup_date: v })} /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INQUIRY_STATUSES.filter((s) => s.value !== 'joined').map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Remarks</Label><Input value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitStatus !== ASYNC_SUBMIT_STATUS.idle}>Cancel</Button>
            <AsyncSubmitButton
              onAction={save}
              onSuccess={() => { setOpen(false); fetchItems(); }}
              onStatusChange={setSubmitStatus}
              className="bg-blue-600"
              loadingLabel="Saving…"
            >
              Save
            </AsyncSubmitButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {convertId && (
        <CrmConvertJoinDialog
          inquiryId={convertId}
          onClose={() => setConvertId(null)}
          onSuccess={() => { setConvertId(null); fetchItems(); }}
        />
      )}
    </div>
  );
}
